import { useDebounce } from "hooks";
import {
  ComponentProps,
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ChildrenProps } from "types";
import Button from "./Button";

type ContextType = { active: string; setActive: (val: string) => void };
type ActiveKeyProps = { activeKey?: string };
type EventKeyProps = { eventKey: string };

const TabContext = createContext({} as ContextType);
function Tabs({ activeKey = "", children }: ChildrenProps & ActiveKeyProps) {
  const [params, setParams] = useSearchParams({ tab: activeKey });
  const active = params.get("tab") ?? activeKey;
  const setActive = (tab: string) => {
    setParams((p) => {
      p.set("tab", tab);
      return p;
    });
  };
  return (
    <TabContext.Provider value={{ active, setActive }}>
      {children}
    </TabContext.Provider>
  );
}
function TabButtonGroup({
  className,
  children,
  ...props
}: ComponentProps<"div">) {
  const debounce = useDebounce();
  const divRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const { active } = useContext(TabContext);
  const handleIndicator = useCallback(() => {
    setTimeout(() => {
      const parent = divRef.current;
      const selector = "button.active";
      const activeButton = parent?.querySelector<HTMLButtonElement>(selector);
      const buttonOffsetLeft = activeButton?.offsetLeft ?? 0;
      const buttonWidth = activeButton?.getBoundingClientRect().width ?? 0;
      const left = buttonOffsetLeft;
      const width = buttonWidth;
      indicatorRef.current?.style.setProperty("left", `${left}px`);
      indicatorRef.current?.style.setProperty("width", `${width}px`);
    }, 0);
  }, []);
  useEffect(() => {
    const ms = 100;
    const handleResize = () => {
      debounce(() => handleIndicator(), ms);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleIndicator, debounce]);
  useEffect(handleIndicator, [handleIndicator, active]);
  return (
    <div
      ref={divRef}
      className={twMerge(
        "relative w-full flex items-center bg-theme rounded",
        className,
      )}
      {...props}
    >
      <span
        ref={indicatorRef}
        className="absolute block bottom-0 left-0 w-auto h-px rounded-full px-2 transition-[width,left]"
      >
        <span className="block size-full bg-[#353740]"></span>
      </span>
      {children}
    </div>
  );
}
function TabButton({
  eventKey,
  className,
  children,
  ...props
}: EventKeyProps & ComponentProps<typeof Button>) {
  const { active, setActive } = useContext(TabContext);
  const isActive = active === eventKey;
  const handleClick = () => {
    setActive(eventKey);
  };
  return (
    <Button
      type="button"
      onClick={handleClick}
      className={twMerge(
        "flex-1 h-full min-w-0 px-1 text-[0.8125rem] font-bold whitespace-nowrap rounded-none first-of-type:rounded-s last-of-type:rounded-e sm:text-sm",
        isActive ? "active text-[#353740]" : "text-[#9A9EB1]",
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}
function TabItem({ eventKey, children }: ChildrenProps & EventKeyProps) {
  const { active } = useContext(TabContext);
  const isActive = active === eventKey;
  return <Fragment>{isActive && children}</Fragment>;
}
Tabs.ButtonGroup = TabButtonGroup;
Tabs.Button = TabButton;
Tabs.Item = TabItem;
export default Tabs;
